import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { SplitText } from "gsap/SplitText";
import { useTouchDevice } from "../utils/deviceDetector";
gsap.registerPlugin(SplitText);

const Cornerinfo = forwardRef(
	(
		{
			topLine = "Dev Local time (UTC +6:00)",
			description,
			cornerH2,
			clientTime = false,
		},
		ref,
	) => {
		const [timeString, seTimeString] = useState();
		const [am, setAsAM] = useState(false);
		const [pm, setAsPM] = useState(false);
		const [seconds, setSeconds] = useState(0);
		const [fillColor, setFillColor] = useState("#0000000");
		const cornerAnimationRef = useRef();

		useEffect(() => {
			/**
			 * This is to getrid of Flush of Null time on first render
			 */
			// N:1
			const updateTime = () => {
				const tm = new Intl.DateTimeFormat("en-US", {
					timeZone: "Asia/Dhaka",
					hour: "numeric",
					minute: "numeric",
					second: "numeric",
					hour24: false,
				});
				const fullString = tm.format(new Date());
				/** Separate time and am-pm */
				const [timePart, ampm] = fullString.split(" ");
				const [h, m, s] = timePart.split(":");

				// State updates
				seTimeString(`${h}:${m}`);
				setAsAM(ampm === "AM");
				setAsPM(ampm === "PM");
				setSeconds(s);
				setFillColor(seconds % 2 === 0 ? "limegreen" : "#0000000");
			};
			// N:2
			updateTime();

			/**
			 * Interval
			 */
			const intervalID = setInterval(updateTime, 1000);
			return () => clearInterval(intervalID);
		}, [seconds]);

		/***
		 * ANIMATION
		 */
		useGSAP(
			() => {
				gsap.fromTo(
					"#heading2",
					{ y: 10, opacity: 0 },
					{ duration: 0.4, y: 0, opacity: 1, ease: "elastic.inOut" },
				);
				gsap.fromTo(
					".description",
					{ y: 10, opacity: 0 },
					{ y: 0, opacity: 1, duration: 0.6, ease: "elastic.inOut" },
				);
			},
			{
				dependencies: [description, cornerH2],
				scope: cornerAnimationRef.current,
				revertOnUpdate: true,
			},
		);

		const touchDevice = useTouchDevice();

		/***
		 * COMPONENT
		 */
		return (
			<div
				className="corner_container"
				ref={ref}
				style={{
					width: clientTime ? "auto" : touchDevice ? "240px" : "270px",
					position: clientTime ? "relative" : "fixed",
					bottom: clientTime ? "none" : "calc(var(--gutter) + 50px)",
					left: clientTime ? "none" : "calc(var(--gutter) + 16px)",
					backdropFilter: clientTime ? "none" : "blur(32px)",
					WebkitBackdropFilter: clientTime ? "none" : "blur(32px)",
					borderRadius: clientTime ? "0px" : "8px",
					padding: clientTime ? "0px" : "10px",
					zIndex: "10",
				}}
			>
				<div
					className="cornerinfo"
					style={{
						height: clientTime ? "auto" : "auto",
					}}
					ref={cornerAnimationRef}
				>
					<p>{topLine}</p>
					<div className="time_svg">
						<p className="mainTime">{timeString}</p>
						<div className="amPm">
							<svg
								width="31"
								height="9"
								viewBox="0 0 31 9"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g id="am">
									<circle
										id="Ellipse_am"
										cx="4.5"
										cy="4.5"
										r="4"
										stroke="#0F0F0F"
										fill={am ? fillColor : "#0000000"}
									/>
									<path
										id="am_2"
										d="M16.8 4.64C16.72 4.704 16.568 4.768 16.344 4.832C16.12 4.896 15.824 4.968 15.456 5.048C15.184 5.096 14.876 5.148 14.532 5.204C14.228 5.252 13.968 5.372 13.752 5.564C13.544 5.748 13.44 5.992 13.44 6.296C13.44 6.56 13.532 6.792 13.716 6.992C13.908 7.184 14.136 7.3 14.4 7.34C14.68 7.38 14.92 7.384 15.12 7.352C15.32 7.32 15.552 7.228 15.816 7.076C16.08 6.932 16.288 6.744 16.44 6.512C16.592 6.272 16.696 6.02 16.752 5.756C16.784 5.564 16.8 5.368 16.8 5.168V4.64ZM18.468 8H17.724C17.516 8 17.348 7.936 17.22 7.808C17.092 7.68 17.028 7.512 17.028 7.304C17.028 7.136 17.044 6.968 17.076 6.8C17.108 6.624 17.148 6.468 17.196 6.332H17.028C16.932 6.596 16.792 6.856 16.608 7.112C16.432 7.36 16.228 7.56 15.996 7.712C15.716 7.896 15.412 8.02 15.084 8.084C14.756 8.148 14.408 8.16 14.04 8.12C13.768 8.088 13.512 8 13.272 7.856C13.04 7.704 12.852 7.504 12.708 7.256C12.564 7.008 12.492 6.732 12.492 6.428C12.492 5.948 12.624 5.576 12.888 5.312C13.152 5.048 13.484 4.868 13.884 4.772C14.068 4.732 14.268 4.696 14.484 4.664C14.7 4.624 14.904 4.592 15.096 4.568C15.472 4.512 15.74 4.468 15.9 4.436L16.056 4.412C16.304 4.348 16.484 4.276 16.596 4.196C16.708 4.108 16.744 3.98 16.704 3.812C16.664 3.636 16.576 3.472 16.44 3.32C16.304 3.168 16.124 3.048 15.9 2.96C15.684 2.864 15.436 2.816 15.156 2.816C14.836 2.816 14.548 2.88 14.292 3.008C14.036 3.128 13.84 3.292 13.704 3.5C13.568 3.708 13.5 3.936 13.5 4.184H12.54C12.54 3.8 12.644 3.444 12.852 3.116C13.06 2.78 13.364 2.512 13.764 2.312C14.164 2.104 14.632 2 15.168 2C15.704 2 16.168 2.104 16.56 2.312C16.96 2.512 17.264 2.788 17.472 3.14C17.68 3.492 17.784 3.88 17.784 4.304V6.884C17.784 6.948 17.804 7.004 17.844 7.052C17.892 7.092 17.952 7.112 18.024 7.112H18.468V8ZM28.9871 3.488C29.1231 3.824 29.1911 4.164 29.1911 4.508V8H28.2071V4.604C28.2071 4.164 28.0951 3.78 27.8711 3.452C27.6551 3.116 27.3231 2.916 26.8751 2.852C26.4831 2.788 26.1471 2.836 25.8671 2.996C25.5871 3.156 25.3631 3.384 25.1951 3.68C25.0271 3.968 24.9111 4.292 24.8471 4.652C24.7991 5.052 24.7751 5.468 24.7751 5.9V8H23.8631V4.604C23.8631 4.148 23.7591 3.76 23.5511 3.44C23.3431 3.12 23.0071 2.924 22.5431 2.852C22.1511 2.796 21.8151 2.848 21.5351 3.008C21.2551 3.16 21.0271 3.384 20.8511 3.68C20.6831 3.976 20.5671 4.3 20.5031 4.652L20.4911 4.712C20.4751 4.936 20.4591 5.128 20.4431 5.288C20.4351 5.44 20.4311 5.644 20.4311 5.9V8H19.4471V2.144H20.4311V2.588C20.4311 2.78 20.4071 2.996 20.3591 3.236C20.3191 3.468 20.2671 3.684 20.2031 3.884H20.3711C20.4991 3.42 20.7031 3.036 20.9831 2.732C21.2631 2.428 21.5871 2.22 21.9551 2.108C22.3311 1.988 22.7191 1.976 23.1191 2.072C23.4791 2.152 23.7631 2.312 23.9711 2.552C24.1791 2.784 24.3191 3.028 24.3911 3.284C24.4711 3.532 24.5031 3.732 24.4871 3.884H24.6551C24.7991 3.42 25.0151 3.036 25.3031 2.732C25.5911 2.428 25.9191 2.216 26.2871 2.096C26.6631 1.976 27.0551 1.968 27.4631 2.072C27.8311 2.16 28.1431 2.336 28.3991 2.6C28.6631 2.856 28.8591 3.152 28.9871 3.488Z"
										fill="black"
									/>
								</g>
							</svg>
							<svg
								width="31"
								height="9"
								viewBox="0 0 31 9"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g id="pm">
									<circle
										id="Ellipse_pm"
										cx="4.5"
										cy="4.5"
										r="4"
										stroke="#0F0F0F"
										fill={pm ? fillColor : "#0000000"}
									/>
									<path
										id="pm_2"
										d="M17.436 5.072C17.436 4.44 17.268 3.92 16.932 3.512C16.604 3.096 16.148 2.888 15.564 2.888C15.172 2.888 14.836 2.984 14.556 3.176C14.276 3.368 14.06 3.632 13.908 3.968C13.764 4.296 13.696 4.664 13.704 5.072C13.712 5.704 13.88 6.228 14.208 6.644C14.536 7.052 14.988 7.256 15.564 7.256C15.956 7.256 16.292 7.16 16.572 6.968C16.852 6.776 17.064 6.516 17.208 6.188C17.36 5.852 17.436 5.48 17.436 5.072ZM18.42 5.072C18.42 5.608 18.324 6.108 18.132 6.572C17.948 7.036 17.66 7.416 17.268 7.712C16.884 8 16.408 8.144 15.84 8.144C15.28 8.144 14.828 8.012 14.484 7.748C14.14 7.484 13.876 7.14 13.692 6.716H13.536C13.648 7.156 13.704 7.624 13.704 8.12V11.336H12.72V2.144H13.704V2.264C13.704 2.456 13.688 2.656 13.656 2.864C13.624 3.072 13.584 3.256 13.536 3.416H13.692C13.716 3.368 13.748 3.304 13.788 3.224C13.908 2.984 14.056 2.776 14.232 2.6C14.416 2.416 14.644 2.272 14.916 2.168C15.188 2.056 15.504 2 15.864 2C16.424 2 16.896 2.148 17.28 2.444C17.664 2.732 17.948 3.108 18.132 3.572C18.324 4.036 18.42 4.536 18.42 5.072ZM29.1278 3.488C29.2638 3.824 29.3318 4.164 29.3318 4.508V8H28.3478V4.604C28.3478 4.164 28.2358 3.78 28.0118 3.452C27.7958 3.116 27.4638 2.916 27.0158 2.852C26.6238 2.788 26.2878 2.836 26.0078 2.996C25.7278 3.156 25.5038 3.384 25.3358 3.68C25.1678 3.968 25.0518 4.292 24.9878 4.652C24.9398 5.052 24.9158 5.468 24.9158 5.9V8H24.0038V4.604C24.0038 4.148 23.8998 3.76 23.6918 3.44C23.4838 3.12 23.1478 2.924 22.6838 2.852C22.2918 2.796 21.9558 2.848 21.6758 3.008C21.3958 3.16 21.1678 3.384 20.9918 3.68C20.8238 3.976 20.7078 4.3 20.6438 4.652L20.6318 4.712C20.6158 4.936 20.5998 5.128 20.5838 5.288C20.5758 5.44 20.5718 5.644 20.5718 5.9V8H19.5878V2.144H20.5718V2.588C20.5718 2.78 20.5478 2.996 20.4998 3.236C20.4598 3.468 20.4078 3.684 20.3438 3.884H20.5118C20.6398 3.42 20.8438 3.036 21.1238 2.732C21.4038 2.428 21.7278 2.22 22.0958 2.108C22.4718 1.988 22.8598 1.976 23.2598 2.072C23.6198 2.152 23.9038 2.312 24.1118 2.552C24.3198 2.784 24.4598 3.028 24.5318 3.284C24.6118 3.532 24.6438 3.732 24.6278 3.884H24.7958C24.9398 3.42 25.1558 3.036 25.4438 2.732C25.7318 2.428 26.0598 2.216 26.4278 2.096C26.8038 1.976 27.1958 1.968 27.6038 2.072C27.9718 2.16 28.2838 2.336 28.5398 2.6C28.8038 2.856 28.9998 3.152 29.1278 3.488Z"
										fill="black"
									/>
								</g>
							</svg>
						</div>
					</div>
					{clientTime ? (
						<></>
					) : (
						<>
							<p id="heading2" className="heading2">
								{cornerH2}
							</p>
							<p className="description">{description}</p>
						</>
					)}
				</div>
			</div>
		);
	},
);

export default Cornerinfo;
